import { useParams, Link, Navigate } from 'react-router-dom';
import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import { doelgroepenData } from '../../../mocks/voor-wie-data';
import { useSEO } from '../../../hooks/useSEO';
import { SITE_URL } from '../../../utils/siteConfig';

function VoorWieDetailSEO({ slug, title, description }: { slug: string; title: string; description: string }) {
  useSEO({
    title: `${title} Inkopen uit China | DUC - Direct Uit China`,
    description,
    keywords: `${title.toLowerCase()} inkopen China, ${title.toLowerCase()} producten importeren, sourcing agent ${title.toLowerCase()}, DUC Direct Uit China`,
    canonical: `${SITE_URL}/voor-wie/${slug}`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Voor wie', item: `${SITE_URL}/voor-wie` },
          { '@type': 'ListItem', position: 3, name: title, item: `${SITE_URL}/voor-wie/${slug}` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: `${title} — DUC Direct Uit China`,
        description,
        url: `${SITE_URL}/voor-wie/${slug}`,
        inLanguage: 'nl-NL',
      },
    ],
  });
  return null;
}

export default function VoorWieDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const data = doelgroepenData.find((d) => d.slug === slug);
  const heroRef = useRef<HTMLDivElement>(null);
  const heroInView = useInView(heroRef, { once: true });
  const contentRef = useRef<HTMLDivElement>(null);
  const contentInView = useInView(contentRef, { once: true, margin: '-60px' });

  if (!data) return <Navigate to="/voor-wie" replace />;

  return (
    <div className="min-h-screen bg-white">
      <VoorWieDetailSEO slug={data.slug} title={data.title} description={data.description} />
      <Navbar />

      <section ref={heroRef} className="relative bg-[#0A1628] pt-[100px] sm:pt-[116px] lg:pt-[136px] pb-14 md:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={data.image} alt={data.title} className="w-full h-full object-cover object-top opacity-30" />
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/80 to-[#0A1628]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-3 mb-8 flex-wrap">
              <Link to="/" className="text-white/40 hover:text-[#3B9EE8] text-sm transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-white/25 text-sm" />
              <Link to="/voor-wie" className="text-white/40 hover:text-[#3B9EE8] text-sm transition-colors cursor-pointer">Voor wie</Link>
              <i className="ri-arrow-right-s-line text-white/25 text-sm" />
              <span className="text-[#3B9EE8] text-sm">{data.title}</span>
            </div>
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.span initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 }} className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
                <i className={`${data.icon}`} />
                {data.subtitle}
              </motion.span>
              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.15 }} className="font-heading font-800 text-white text-4xl md:text-5xl leading-tight mb-6">
                DUC voor<br /><span className="text-[#3B9EE8]">{data.title}</span>
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.25 }} className="text-white/65 text-lg leading-relaxed mb-8">
                {data.description}
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.35 }} className="flex flex-wrap gap-4">
                <a href="/#contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-7 py-3.5 rounded-lg font-700 text-sm transition-all duration-200 cursor-pointer whitespace-nowrap">
                  <i className="ri-mail-send-line" />
                  Gratis offerte aanvragen
                </a>
                <a href="https://wa.me/31624359162" target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 border border-white/25 text-white hover:border-[#3B9EE8]/60 hover:text-[#3B9EE8] px-7 py-3.5 rounded-lg font-600 text-sm transition-all duration-200 cursor-pointer whitespace-nowrap">
                  <i className="ri-whatsapp-line text-lg" />
                  WhatsApp
                </a>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={heroInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-wrap gap-4">
              {data.stats.map((stat) => (
                <div key={stat.label} className="bg-white/8 border border-white/12 rounded-xl px-6 py-5 flex-1 min-w-[120px]">
                  <div className="font-heading font-800 text-[#3B9EE8] text-2xl mb-1">{stat.value}</div>
                  <div className="text-white/50 text-xs">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section ref={contentRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16 items-start mb-20">
            <motion.div initial={{ opacity: 0, x: -24 }} animate={contentInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6 }}>
              <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-6">
                Hoe DUC helpt bij<br /><span className="text-[#3B9EE8]">{data.title}</span>
              </h2>
              <p className="text-[#0A1628]/65 text-base leading-relaxed mb-8">{data.longDescription}</p>
              <div className="bg-[#0A1628] rounded-xl p-6">
                <p className="text-[#3B9EE8] text-xs font-700 uppercase tracking-widest mb-2">Ons motto</p>
                <p className="text-white font-600 text-lg italic">&ldquo;Van inkoop tot aftercare, volledig geregeld&rdquo;</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 24 }} animate={contentInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.1 }}>
              <h3 className="font-heading font-700 text-[#0A1628] text-xl mb-6">Producten die wij leveren</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {data.products.map((product) => (
                  <div key={product} className="flex items-center gap-3 bg-[#F7F8FA] border border-[#0A1628]/6 rounded-xl p-3.5 hover:border-[#3B9EE8]/30 transition-colors">
                    <i className="ri-check-line text-[#3B9EE8] text-sm flex-shrink-0" />
                    <span className="text-[#0A1628]/75 text-sm font-500">{product}</span>
                  </div>
                ))}
              </div>
              <h3 className="font-heading font-700 text-[#0A1628] text-xl mb-4">Voordelen voor {data.title}</h3>
              <ul className="space-y-3">
                {data.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#3B9EE8]/15 flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[#3B9EE8] text-xs" />
                    </div>
                    <span className="text-[#0A1628]/70 text-sm leading-relaxed">{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          <div className="bg-[#F7F8FA] rounded-2xl p-6 mb-6 border border-[#0A1628]/6">
            <p className="text-[#0A1628]/50 text-xs flex items-center gap-2">
              <i className="ri-information-line text-[#3B9EE8]" />
              Alle genoemde prijzen zijn exclusief BTW (ex. BTW), tenzij anders vermeld.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {doelgroepenData.filter((d) => d.slug !== slug).slice(0, 4).map((related) => (
              <Link key={related.slug} to={`/voor-wie/${related.slug}`} className="group bg-white border border-[#0A1628]/6 hover:border-[#3B9EE8]/40 rounded-xl p-5 transition-all duration-200 cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#3B9EE8]/10 mb-3">
                  <i className={`${related.icon} text-[#3B9EE8] text-lg`} />
                </div>
                <h4 className="font-700 text-[#0A1628] text-sm mb-1 group-hover:text-[#3B9EE8] transition-colors">{related.title}</h4>
                <p className="text-[#0A1628]/45 text-xs">{related.subtitle}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0A1628]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-800 text-white text-3xl md:text-4xl mb-4">{data.cta}</h2>
          <p className="text-white/50 text-base mb-8">Gratis en vrijblijvend. Reactie binnen 24 uur.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/#contact" className="inline-flex items-center justify-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-8 py-4 rounded-lg font-700 transition-all duration-200 cursor-pointer whitespace-nowrap">
              <i className="ri-mail-send-line" />
              Gratis offerte aanvragen
            </a>
            <Link to="/voor-wie" className="inline-flex items-center justify-center gap-2 border border-white/25 text-white hover:border-[#3B9EE8]/60 hover:text-[#3B9EE8] px-8 py-4 rounded-lg font-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line" />
              Alle doelgroepen
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
